<template>
    <div id="ticket-history">
        <div id="visitor-history-row">
            <div class="left">
                <div class="pagination-toolbar">
                    <div v-if="ticketHistory.count > 0">
                        {{ Math.min(Math.max(1, (queries.pageCount - 1) * queries.perPage), ticketHistory.count) }} -
                        {{ Math.min(Math.max(1, queries.pageCount * queries.perPage), ticketHistory.count) }} of
                        {{ ticketHistory.count }} tickets
                    </div>
                    <a-button-group>
                        <a-button
                            icon="left"
                            :disabled="ticketHistory.count === 0 || !prevAvailable()"
                            @click="prevPage"
                        />
                        <a-button
                            icon="right"
                            :disabled="ticketHistory.count === 0 || !nextAvailable()"
                            @click="nextPage"
                        />
                    </a-button-group>

                    <a-popover
                        placement="bottom"
                        overlayClassName="btnPerPagePopover"
                        :visible="pageSizeChangerVisible"
                        @visibleChange="pageSizeChangerVisible = !pageSizeChangerVisible"
                    >
                        <template slot="content">
                            <div class="page-options">
                                <div
                                    v-for="val of [1, 10, 20, 30, 50, 75, 100, 150, 200]"
                                    @click="changePerPage(val)"
                                    :key="val"
                                    class="item"
                                >
                                    <span class="value">{{ val }}</span>
                                </div>
                            </div>
                        </template>
                        <a-button class="btnPerPage" @click="pageSizeChangerVisible = true">
                            <SortIcon />
                            {{ $t("message.perPageInFilter") }}: {{ queries.perPage }}
                            <a-icon type="caret-down" />
                        </a-button>
                    </a-popover>
                </div>
            </div>
            <div class="right">
                <b-form-group
                    id="start-date-input-group"
                    :label="$t('message.first')"
                    class="custom-input"
                    label-for="start-date-input"
                    label-sr-only
                >
                    <datetime
                        v-model="queries.dateBegin"
                        :max-datetime="maxDate"
                        :min-datetime="lastFiveYear"
                        :phrases="phrases"
                        :placeholder="$t('message.startDate')"
                        auto
                        input-class="form-control rounded-0"
                        input-id="start-date-input"
                        type="datetime"
                    />
                </b-form-group>
                <b-form-group
                    id="end-date-input-group"
                    :label="$t('message.last')"
                    class="custom-input"
                    label-for="end-date-input"
                    label-sr-only
                >
                    <datetime
                        v-model="queries.dateEnd"
                        :max-datetime="maxDate"
                        :min-datetime="lastFiveYear"
                        :phrases="phrases"
                        :placeholder="$t('message.endDate')"
                        auto
                        input-class="form-control rounded-0"
                        input-id="end-date-input"
                        type="datetime"
                    />
                </b-form-group>
                <b-form-group
                    id="query-input-group"
                    :label="$t('message.query')"
                    class="custom-input"
                    label-for="query-input"
                    label-sr-only
                >
                    <b-form-input
                        id="query-input"
                        v-model="queries.query"
                        :placeholder="$t('message.query')"
                        class="rounded-0"
                        name="query"
                    />
                </b-form-group>

                <b-form-group class="custom-input">
                    <a-rate v-model="queries.rate" :allow-clear="true" />
                </b-form-group>
                <b-form-group class="custom-input">
                    <b-form-select v-model="queries.assignedUser" class="mb-3">
                        <option :value="null">
                            {{ $t("message.allUsers") }}
                        </option>
                        <option v-for="item in usersFilter.data" :key="item._id" :value="item._id">
                            {{ item.name }}
                        </option>
                    </b-form-select>
                </b-form-group>

                <b-form-group class="custom-input">
                    <b-form-select v-model="queries.status" class="mb-3">
                        <option :value="null">
                            {{ $t("message.allStatus") }}
                        </option>
                        <option v-for="item in statuses" :key="item._id" :value="item.title">
                            {{ item.title }}
                        </option>
                    </b-form-select>
                </b-form-group>
            </div>
        </div>

        <ArchiveComponent
            :type="'ticket'"
            :hide-filters="true"
            :is-archive="false"
            :selected-time="timeSelect"
            :table-data="
                ticketHistory.tickets.map((x) => ({
                    ...x,
                    fullname: !!x.osrPayload
                        ? x.osrPayload['fullname']
                        : !!x.conversation
                        ? x.conversation['fullname']
                        : '-',
                    email: !!x.osrPayload ? x.osrPayload['email'] : !!x.conversation ? x.conversation['email'] : '-',
                    phone: !!x.osrPayload ? x.osrPayload['phone'] : !!x.conversation ? x.conversation['phone'] : '-',
                }))
            "
        />
    </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters, mapState } from "vuex";
import { tableSorter } from "../../../../utils/utils";
import { log2 } from "../../../../utils/log2";
import * as _ from "lodash";
import { toNewMessagesModel } from "../../../../utils/chat";
import MessageList from "../../../../components/popup-chat/MessageList.vue";
import SearchIcon from "../../../../components/svg/SearchIcon.vue";
import Pagination from "../../../../components/Pagination.vue";
import ArchiveComponent from "../../../../components/ArchiveComponent.vue";
import SortIcon from "../../../../components/svg/SortIcon.vue";

const log = log2("TicketHistory");
const defaultStartDate = moment().startOf("day").toISOString();
const defaultEndDate = moment().endOf("day").toISOString();
export default {
    name: "TicketHistory",
    components: {
        Pagination,
        MessageList,
        SearchIcon,
        ArchiveComponent,
        SortIcon,
    },
    props: {
        timeSelect: { type: String },
        statuses: { type: Array },
    },
    data() {
        return {
            pageSizes: ["10", "25", "50", "100"],
            lastFiveYear: moment().subtract(5, "year").toISOString(),
            usersFilter: [],
            pageSizeChangerVisible: false,
            queries: {
                dateBegin: defaultStartDate,
                dateEnd: defaultEndDate,
                query: null,
                rate: undefined,
                assignedUser: null,
                status: null,
                pageCount: 1,
                perPage: 10,
            },
            phrases: {
                ok: this.$t("message.ok"),
                cancel: this.$t("message.cancel"),
            },
            maxDate: moment().toISOString(),
            selectedHistory: {
                visitor: {},
                messages: [],
                ticket: {},
            },
            columns: [
                {
                    title: "Email",
                    dataIndex: "conversation.email",
                    key: "age",
                    ...tableSorter("conversation.email", "string", this.currentSort),
                },
                {
                    dataIndex: "conversation",
                    title: this.$t("message.name"),
                    key: "conversation",
                    scopedSlots: { customRender: "conversation" },
                    ...tableSorter("conversation.fullName", "string", this.currentSort),
                },
                {
                    dataIndex: "createdDate",
                    title: this.$t("message.date"),
                    scopedSlots: { customRender: "createdDate" },
                    slot: "createdDate",
                    ...tableSorter("createdDate", "date", this.currentSort),
                },
                {
                    title: this.$t("message.subject"),
                    dataIndex: "tag",
                    key: "tag",
                    ...tableSorter("tag", "string", this.currentSort),
                },
                {
                    dataIndex: "assignedUser",
                    title: "Temsilci",
                    scopedSlots: { customRender: "assignedUser" },
                    slot: "assignedUser",
                    width: "10%",
                    ...tableSorter("assignedUser.name", "string", this.currentSort),
                },
                {
                    dataIndex: "_id",
                    title: this.$t("message.transactions"),
                    scopedSlots: { customRender: "_id" },
                    slot: "_id",
                    width: "15%",
                    ...tableSorter("_id", "string", this.currentSort),
                },
            ],
            colors: {
                header: {
                    bg: "#4e8cff",
                    text: "#ffffff",
                },
                launcher: {
                    bg: "#4e8cff",
                },
                messageList: {
                    bg: "#ffffff",
                },
                sentMessage: {
                    bg: "#4e8cff",
                    text: "#ffffff",
                },
                receivedMessage: {
                    bg: "#eaeaea",
                    text: "#222222",
                },
                userInput: {
                    bg: "#f4f7f9",
                    text: "#565867",
                },
            },
        };
    },
    computed: {
        ...mapGetters({
            ticketHistoryGetter: "ticket/getTicketHistory",
        }),
        ...mapState({
            ticketHistory: (state) => state.ticket.ticketHistory,
        }),
        filteredMessages() {
            log(
                "filteredMessages",
                this.selectedHistory.messages,
                this.selectedHistory.messages.filter((m) => "undefined" === typeof m.type)
            );
            return this.selectedHistory.messages.filter((m) => "undefined" === typeof m.type);
        },
    },
    methods: {
        ...mapActions({
            getUsers: "user/getUsers",
            fetchTicketHistory: "ticket/fetchTicketHistory",
            loadOldMessages: "chat/loadOldMessages",
        }),
        updateQueryParams(key, value) {
            if (value === "" || value == null) {
                let query = Object.assign({}, this.$route.query);
                delete query[key];
                this.$router.replace({ query });
            } else {
                let { query } = this.$route;
                this.$router.replace({
                    query: { ...query, [key]: encodeURI(value) },
                });
            }
        },
        changePerPage(value) {
            this.queries.perPage = value;
            this.queries.pageCount = 1;
            this.pageSizeChangerVisible = false;
        },
        nextAvailable() {
            return this.queries.pageCount < this.ticketHistory.totalPages;
        },
        prevAvailable() {
            return this.queries.pageCount > 1;
        },
        nextPage() {
            if (this.nextAvailable()) {
                this.queries.pageCount++;
            }
        },
        prevPage() {
            if (this.prevAvailable()) {
                this.queries.pageCount--;
            }
        },
        fetchAllUsers() {
            this.getUsers({ perPage: 1000, page: 1 }).then((response) => {
                log("getUsers completed", response.data);
                this.usersFilter = response.data;
            });
        },
        setCustomDateFilter(filter) {
            log.debug("setCustomDateFilter ", filter);
            if (filter === "today") {
                this.queries.dateBegin = moment().startOf("day").toISOString();
                this.queries.dateEnd = moment().endOf("day").toISOString();
            } else if (filter === "yesterday") {
                this.queries.dateBegin = moment().subtract(1, "days").startOf("day").toISOString();
                this.queries.dateEnd = moment().subtract(1, "days").endOf("day").toISOString();
            } else if (filter === "current-week") {
                this.queries.dateBegin = moment().startOf("week").startOf("day").toISOString();
                this.queries.dateEnd = moment().endOf("day").toISOString();
            } else if (filter === "last-week") {
                this.queries.dateBegin = moment()
                    .startOf("isoWeeks")
                    .subtract(1, "days")
                    .startOf("isoWeeks")
                    .toISOString();
                this.queries.dateEnd = moment().startOf("isoWeeks").subtract(1, "days").endOf("isoWeeks").toISOString();
            } else if (filter === "current-month") {
                this.queries.dateBegin = moment().startOf("month").startOf("day").toISOString();
                this.queries.dateEnd = moment().endOf("day").toISOString();
            } else if (filter === "last-month") {
                this.queries.dateBegin = moment().startOf("month").subtract(1, "days").startOf("month").toISOString();
                this.queries.dateEnd = moment().startOf("month").subtract(1, "days").endOf("month").toISOString();
            } else if (filter === "last-30") {
                this.queries.dateBegin = moment().subtract(30, "days").startOf("day").toISOString();
                this.queries.dateEnd = moment().endOf("day").toISOString();
            } else if (filter === "all-time") {
                this.queries.dateBegin = null;
                this.queries.dateEnd = null;
            }
        },
        view(item) {
            this.selectedHistory.ticket = item;
            this.selectedHistory.visitor = item.visitor;
            this.loadOldMessages(item.conversation._id).then((response) => {
                let items = response.data.data;
                items = _.orderBy(
                    _.uniqBy(items, (q) => q.uniqMessageId || q._id),
                    (q) => moment(q.createdDate).toDate().getTime(),
                    "asc"
                );
                this.selectedHistory.messages = items;
                this.$bvModal.show("history-ticket-details");
            });
        },
        getMessages() {
            let allMessages = toNewMessagesModel(this.filteredMessages, this);

            if (this.typingMessage) {
                allMessages = [
                    ...allMessages,
                    {
                        type: "typing",
                        data: {
                            text: this.typingMessage,
                        },
                    },
                ];
            }

            return allMessages;
        },
        getParticipants() {
            const supportUsers = this.filteredMessages
                .filter((x) => x.messageType === "user" || x.messageType === "private")
                .map((x) => ({
                    id: x.userId,
                    name: x.username,
                }));

            return _.uniqBy(supportUsers, (q) => q.name);
        },
        reset() {
            this.selectedHistory = {
                visitor: {},
                messages: [],
                ticket: {},
            };
        },
        pageChange(page, perPage) {
            log("pageChange", { page, perPage });
            this.queries.pageCount = page;
            this.queries.perPage = perPage;
        },
        fetch(queries) {
            this.updateQueryParams("page", queries.pageCount);
            this.updateQueryParams("perPage", queries.perPage);
            this.fetchTicketHistory({ ...queries });
        },
    },
    mounted() {
        moment.locale(this.$i18n.locale);
        this.fetchAllUsers();
        this.setCustomDateFilter(this.timeSelect);
        this.fetch(this.queries);
        this.refreshTimerId = setInterval(() => {
            this.fetchTicketHistory({ ...this.queries });
        }, 1000 * 10);
    },
    beforeDestroy() {
        clearInterval(this.refreshTimerId);
    },
    watch: {
        queries: {
            handler(newValue) {
                this.fetch({ ...newValue });
            },
            deep: true,
        },
        timeSelect(newValue) {
            this.setCustomDateFilter(newValue);
        },
    },
};
</script>

<style lang="scss">
.btnPerPagePopover {
    .ant-popover-inner-content {
        padding: 0;
    }

    .page-options {
        display: flex;
        flex-direction: column;
        align-items: center;

        .item {
            min-width: 150px;
            cursor: pointer;
            padding: 10px;

            .value {
                padding: 10px;
            }

            &:hover {
                background: var(--color-bg-hover);
            }
        }
    }
}

#ticket-history {
    display: flex;
    flex-direction: column;
    gap: 10px;

    #visitor-history-row {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        min-height: 30px;
        height: auto;
        flex-wrap: wrap;
        gap: 10px;

        .right {
            display: flex;
            gap: 10px;

            .rate-wrapper {
                background: #fff;
            }
        }
    }

    .pagination-toolbar {
        display: flex;
        align-items: center;
        gap: 10px;

        .btnPerPage {
            display: flex;
            align-items: center;
            gap: 3px;

            svg {
                fill: var(--color-text-default);

                path {
                    fill: var(--color-text-default);
                }
            }

            &:hover {
                svg {
                    fill: var(--color-text-primary-default);

                    path {
                        fill: var(--color-text-primary-default);
                    }
                }
            }
        }
    }

    .custom-input input,
    .custom-input ul,
    .custom-input select {
        background-color: #ffffff;
        border: none;
        border-radius: 6px !important;
        height: 30px;
    }

    .actionButton {
        border: none;
        box-shadow: none;
    }

    .custom-input ul {
        padding: 4px 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
    }

    .userInfo {
        font-family: Poppins;

        &.first {
            color: #3699ff;
            font-size: 16px;
            font-weight: 500;
            padding-top: 15px;
            line-height: 1;
        }

        &.sec {
            color: #181c32;
            font-size: 14px;
            font-weight: 300;
        }
    }

    .custom-table {
        background-color: #ffffff;
        border-radius: 6px;
        padding: 10px 20px;
        overflow-y: auto;
    }

    .ant-table-column-sorters,
    .ant-table-header-column {
        width: 100%;
    }

    .ant-table-header-column {
        padding: 0 10px;
    }

    .table-custom,
    .ant-table-placeholder,
    .ant-table-small {
        border: none;
    }

    .ant-table-thead tr {
    }

    .ant-table-body {
        tr {
        }

        td {
            padding: 0 8px !important;
            font-size: 12px;
            font-weight: 300;
            color: #181c32;
            border-bottom: none;
        }

        th {
            border-right: 1px solid #ebedf3;
            border-bottom: none;
            padding: 0 !important;

            &:last-child {
                border-right: none;
            }
        }
    }

    .ant-table-body tbody td {
        border-right: 1px solid #ebedf3;

        &:last-child {
            border-right: none;
        }
    }

    .ant-table-column-title {
        color: #181c32;
        font-size: 13px;
        font-weight: 300;
        font-family: Poppins;
        white-space: nowrap;
    }

    .ant-table-body tbody td {
        border-right: 1px solid #ebedf3;

        &:last-child {
            border-right: none;
        }
    }

    .ant-table-column-title {
        color: #181c32;
        font-size: 13px;
        font-weight: 300;
        font-family: Poppins;
        white-space: nowrap;
    }

    .actionButton {
        border: none;
        box-shadow: none;
    }
}

.tickets-messages-history {
    height: 550px !important;
}
</style>
